import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonService } from 'src/app/services/common.service';
import { DrrCcdTypes, DeviceFormModel, DrrCcdDeviceApplicationDto, LookupKeys } from '../technical-data.domain';
import { addRemoveAnimation } from 'src/app/+shared/animations/addRemove.animation';
import { LookupOptionsData } from 'src/app/jurisdiction/jurisdiction.domain';
import { NetzTranslateService } from 'src/app/services/netz-translate.service';
import { Option } from 'src/app/+custom-controls/components/custom-form-control.models';
import { SectionIdentifier } from 'src/app/+shared/components/review-section/review-section.domain';
import { ReviewSectionComponent } from 'src/app/+shared/components/review-section/review-section.component';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { DrrCcdDataStore } from '../../drr-ccd-data.store';
import { ValidationsService } from 'src/app/services/validations.service';

@Component({
  selector: 'app-drr-ccd-device',
  templateUrl: './drr-ccd-device.component.html',
  styleUrls: ['./drr-ccd-device.component.scss'],
  animations: [addRemoveAnimation],
})
export class DrrCcdDeviceComponent extends DestroyableComponent implements OnInit {
  form: FormGroup;

  typeofCcdOptions: LookupOptionsData[];
  typeofDrrOptions: LookupOptionsData[];
  typeofHeatingPump: LookupOptionsData[];
  moduleTypes: LookupOptionsData[];
  airConditioningTypes: LookupOptionsData[];
  wallBoxTypes: LookupOptionsData[];

  typeOfControlOptions: Option[];

  showBewareNoteforSeveralHeatPumpsOrAirConditioners: boolean;
  showBewareNoteforWallbox: boolean;
  showBewareNoteforStorage: boolean;
  subTitle: string = '';
  isRegistrationDevice: boolean;

  @Output() formInited = new EventEmitter<FormGroup>();
  @Output() remove = new EventEmitter<void>();
  @Input() canBeDeleted: boolean;
  @Input() data: DeviceFormModel;
  @Input() isDetailsView: boolean;
  @Input() applicationId: any;
  @Input() index: number;

  @ViewChild('deviceSection') deviceSection: ReviewSectionComponent;

  SectionIdentifier = SectionIdentifier;

  constructor(
    private fb: FormBuilder,
    private commonService: CommonService,
    private translationService: NetzTranslateService,
    private store: DrrCcdDataStore
  ) {
    super();
    this.preventLeak(this.store.getApplicationDto()).subscribe((data: DrrCcdDeviceApplicationDto) => {
      this.isRegistrationDevice = data.is_registration_device || false;
    });
    this.form = fb.group({});
    this.addAllControls(this.form);
    this.preventLeak(this.form.valueChanges).subscribe(value => this.toggleAllControls(value));
  }

  ngOnInit(): void {
    if (this.data) {
      this.form.patchValue(this.data);
    }

    this.formInited.emit(this.form);
    this.loadLookups();
    this.setRadioButtonOptions();
    this.preventLeak(this.translationService.registerForLanguageChange()).subscribe(() => {
      this.loadLookups();
      this.setRadioButtonOptions();
      this.setSubTitle(this.form.value.ccdType);
    });
    this.setSubTitle(this.form.value.ccdType);
    this.preventLeak(this.form.controls.ccdType.valueChanges).subscribe(value => this.setSubTitle(value));
  }

  initDeviceUnits(heatingPumUnits: FormArray) {
    this.form.addControl('deviceUnits', heatingPumUnits);
    this.form.controls.deviceUnits.valueChanges.subscribe(value => {
      this.sumTotalConsumptionPower(value.units);
    });
  }

  removeDevice() {
    this.remove.emit();
  }

  get deviceTypes(): LookupOptionsData[] {
    return this.isRegistrationDevice ? this.typeofDrrOptions : this.typeofCcdOptions;
  }

  get sectionTitle(): string {
    const part = this.isRegistrationDevice
      ? this.translationService.translateString('drr-ccd-devices.information-on-drr-header')
      : this.translationService.translateString('drr-ccd-devices.information-on-ccd-header');
    return part + (this.subTitle && ' - ') + this.subTitle;
  }

  get showSection35ForStorage(): boolean {
    return !this.isRegistrationDevice && this.form.value.ccdType === DrrCcdTypes.storage;
  }

  get typeOfWallboxLabelKey(): string {
    return this.isRegistrationDevice ? 'drr-ccd-devices.type-of-charging-device-lbl' : 'drr-ccd-devices.type-of-wall-box-lbl';
  }

  private sumTotalConsumptionPower(units: any): void {
    let power = 0;
    units.forEach(unit => {
      power += Number(unit.unitTotalPower ?? 0);
    });
    this.form.controls.totalConsumption.setValue(power, { emitEvent: false });
  }

  private setRadioButtonOptions() {
    this.typeOfControlOptions = [
      { label: this.translationService.translateString('drr-ccd-devices.direct-relais-contacts-lbl'), value: 'typeOfControl_1' },
      {
        label: this.translationService.translateString('drr-ccd-devices.digital-interface-lbl'),
        value: 'typeOfControl_2',
      },
    ];
  }

  private loadLookups() {
    this.typeofDrrOptions = this.commonService.getDropdownData(LookupKeys.RegistrationDeviceType);
    this.typeofCcdOptions = this.commonService.getDropdownData(LookupKeys.ConsumptionDeviceType);
    this.typeofHeatingPump = this.commonService.getDropdownData(LookupKeys.HeatingPumpType);
    this.moduleTypes = this.commonService.getDropdownData(LookupKeys.ModuleChoice);
    this.airConditioningTypes = this.commonService.getDropdownData(LookupKeys.AirConditioningType);
    this.wallBoxTypes = this.commonService.getDropdownData(LookupKeys.WallBoxType);
  }

  private getTotalPowerValidators() {
    const level = 4.2;
    return [Validators.required, this.isRegistrationDevice ? Validators.max(level) : Validators.min(level)];
  }

  controls = {
    id: {
      add: form => form.addControl('id', this.fb.control(null)),
      visible: false,
      toggle: _ => {},
    },
    ccdType: {
      add: form => form.addControl('ccdType', this.fb.control(null, [Validators.required])),
      visible: true,
      label: () => {
        return this.isRegistrationDevice
          ? this.translationService.translateString('drr-ccd-devices.type-of-drr-lbl')
          : this.translationService.translateString('drr-ccd-devices.type-of-ccd-lbl');
      },
      toggle: formValue => {
        this.showBewareNoteforSeveralHeatPumpsOrAirConditioners =
          !this.isRegistrationDevice && [DrrCcdTypes.airConditioner, DrrCcdTypes.heatPump].includes(formValue.ccdType);
        this.showBewareNoteforWallbox = [DrrCcdTypes.wallBox].includes(formValue.ccdType);
        this.showBewareNoteforStorage = [DrrCcdTypes.storage].includes(formValue.ccdType);
      },
    },
    heatingPumpType: {
      add: form => form.addControl('heatingPumpType', this.fb.control(null, [Validators.required])),
      visible: false,
      toggle: formValue => {
        this.controls.heatingPumpType.visible = formValue.ccdType === DrrCcdTypes.heatPump;
        this.disableIfHidden(this.controls.heatingPumpType, this.form.controls.heatingPumpType);
      },
    },
    airConditioningType: {
      add: form => form.addControl('airConditioningType', this.fb.control(null, [Validators.required])),
      visible: false,
      toggle: formValue => {
        this.controls.airConditioningType.visible = formValue.ccdType === DrrCcdTypes.airConditioner;
        this.disableIfHidden(this.controls.airConditioningType, this.form.controls.airConditioningType);
      },
    },
    wallBoxType: {
      add: form => form.addControl('wallBoxType', this.fb.control(null, [Validators.required])),
      visible: false,
      toggle: formValue => {
        this.controls.wallBoxType.visible = [DrrCcdTypes.wallBox, DrrCcdTypes.chargingDevice].includes(formValue.ccdType);
        this.disableIfHidden(this.controls.wallBoxType, this.form.controls.wallBoxType);
      },
    },
    other: {
      add: form => form.addControl('other', this.fb.control(null, [Validators.required])),
      visible: false,
      toggle: formValue => {
        this.controls.other.visible =
          (formValue.ccdType === DrrCcdTypes.heatPump && formValue.heatingPumpType === 'other') ||
          (formValue.ccdType === DrrCcdTypes.airConditioner && formValue.airConditioningType === 'other') ||
          ([DrrCcdTypes.wallBox, DrrCcdTypes.chargingDevice].includes(formValue.ccdType) && formValue.wallBoxType === 'other');
        this.disableIfHidden(this.controls.other, this.form.controls.other);
      },
    },
    totalConsumption: {
      add: form => form.addControl('totalConsumption', this.fb.control(null, this.getTotalPowerValidators())),
      visible: false,
      toggle: formValue => {
        this.controls.totalConsumption.visible = !!formValue.ccdType;
        this.disableIfHidden(this.controls.totalConsumption, this.form.controls.totalConsumption);
      },
    },
    typeOfControl: {
      add: form => form.addControl('typeOfControl', this.fb.control(null, [Validators.required])),
      visible: false,
      toggle: formValue => {
        this.controls.typeOfControl.visible =
          !this.isRegistrationDevice &&
          [DrrCcdTypes.heatPump, DrrCcdTypes.airConditioner, DrrCcdTypes.wallBox, DrrCcdTypes.storage, DrrCcdTypes.chargingDevice].includes(
            formValue.ccdType
          );
        this.disableIfHidden(this.controls.typeOfControl, this.form.controls.typeOfControl);
      },
    },
    module: {
      add: form => form.addControl('module', this.fb.control(null, [Validators.required])),
      visible: false,
      toggle: formValue => {
        this.controls.module.visible =
          !this.isRegistrationDevice &&
          [DrrCcdTypes.heatPump, DrrCcdTypes.airConditioner, DrrCcdTypes.wallBox, DrrCcdTypes.storage, DrrCcdTypes.chargingDevice].includes(
            formValue.ccdType
          );
        this.disableIfHidden(this.controls.module, this.form.controls.module);
      },
    },
    cascadeMeasurementDesired: {
      add: form => form.addControl('cascadeMeasurementDesired', this.fb.control(null, [Validators.required])),
      visible: false,
      toggle: formValue => {
        this.controls.cascadeMeasurementDesired.visible = [
          DrrCcdTypes.heatPump,
          DrrCcdTypes.airConditioner,
          DrrCcdTypes.wallBox,
          DrrCcdTypes.storage,
          DrrCcdTypes.chargingDevice,
        ].includes(formValue.ccdType);
        this.disableIfHidden(this.controls.cascadeMeasurementDesired, this.form.controls.cascadeMeasurementDesired);
      },
    },
    separateMeterRequired: {
      add: form => form.addControl('separateMeterRequired', this.fb.control(null, [Validators.required])),
      visible: false,
      label: () => {
        return this.isRegistrationDevice
          ? this.translationService.translateString('drr-ccd-devices.separate-meter-required-drr-lbl')
          : this.translationService.translateString('drr-ccd-devices.separate-meter-required-lbl');
      },
      toggle: formValue => {
        this.controls.separateMeterRequired.visible = [
          DrrCcdTypes.heatPump,
          DrrCcdTypes.airConditioner,
          DrrCcdTypes.wallBox,
          DrrCcdTypes.storage,
          DrrCcdTypes.chargingDevice,
        ].includes(formValue.ccdType);
        this.disableIfHidden(this.controls.separateMeterRequired, this.form.controls.separateMeterRequired);
      },
    },
    meterNumber: {
      add: form =>
        form.addControl('meterNumber', this.fb.control(null, [ValidationsService.lettersAndNumbersValidator(), Validators.required])),
      visible: false,
      label: () => {
        return this.isRegistrationDevice
          ? this.translationService.translateString('drr-ccd-devices.meter-number-of-drr-lbl')
          : this.translationService.translateString('drr-ccd-devices.meter-number-of-ccd-lbl');
      },
      toggle: formValue => {
        this.controls.meterNumber.visible = formValue.separateMeterRequired === false;
        this.disableIfHidden(this.controls.meterNumber, this.form.controls.meterNumber);
      },
    },
    meter2: {
      add: form => form.addControl('meter2', this.fb.control(null, [ValidationsService.lettersAndNumbersValidator(), Validators.required])),
      visible: false,
      toggle: formValue => {
        this.controls.meter2.visible = formValue.cascadeMeasurementDesired === true;
        this.disableIfHidden(this.controls.meter2, this.form.controls.meter2);
      },
    },
    schemaImage: {
      add: form => {},
      visible: false,
      toggle: formValue => {
        this.controls.schemaImage.visible = formValue.cascadeMeasurementDesired === true;
      },
    },
  };

  private disableIfHidden(controlSettings: any, formControl: AbstractControl) {
    if (!controlSettings.visible && formControl.enabled) {
      formControl.disable();
      formControl.setValue(controlSettings.default ?? null);
    } else if (controlSettings.visible && formControl.disabled) {
      formControl.enable();
    }
  }

  private addAllControls(form: FormGroup) {
    Object.values(this.controls).forEach(control => control.add(form));
  }

  private toggleAllControls(formValue: any) {
    Object.values(this.controls).forEach(control => control.toggle(formValue));
  }

  private setSubTitle(ccdType: any): any {
    this.subTitle = this.deviceTypes.find(option => option.value === ccdType)?.label ?? '';
  }
}
